import { EmberInvite } from "game-common/models"
import { Container, Graphics, Rectangle, Text } from "pixi.js"
import { TextRenderer } from "../text/text_renderer"
import { truncateName } from "./ember_players_panel"

// The prompt an invited player answers: who is asking, and Join / Decline. It docks in the right-hand
// gutter directly under the players chip, above the player list, so the whole "who else is playing"
// business reads as one column.
//
// It is NOT a modal. An invitation is somebody else's idea, arriving while the player is in the middle
// of something -- very likely a wave -- so it must be ignorable. Ignoring it IS an answer: the bar at
// the foot of the panel drains, and at zero the prompt takes itself down without moving anybody. The
// server expires the invitation on the same clock, so the two agree without a second message.
//
// Amber rim, from the same ember-lit family as the role picker and the tutorial bar, because unlike
// everything else in this column it wants an answer.
const WIDTH = 186
const PAD = 8
const CORNER = 6
const LINE_GAP = 4
const BUTTON_H = 16
const BUTTON_GAP = 6
const METER_H = 3
const METER_GAP = 7

const PANEL_COLOR = 0x05070a
const PANEL_ALPHA = 0.9
const RIM = 0xf2a94e
const RIM_ALPHA = 0.7
const TITLE_COLOR = 0xfff3e0
const BODY_COLOR = 0x8d97ad

const ACCEPT_RIM = 0x7be08a
const ACCEPT_TEXT = 0xdcffe2
const DECLINE_RIM = 0x8d97ad
const DECLINE_TEXT = 0xd4d8e0
const BUTTON_FILL = 0x1b2030
const BUTTON_FILL_HOVER = 0x33405e
const METER_TRACK = 0x2a2119

// Fallback line height for a Text that measures as zero, which is every Text under a mocked canvas.
// Without it the panel collapses onto its own buttons in any headless render.
const LINE_H = 10

// How much of its time an invitation has left, 0..1. Exported for its own test: the meter is the only
// thing telling the player that doing nothing is a decision with a deadline.
export const inviteProgress = (remainingMs: number, totalMs: number): number => {
    if (!(totalMs > 0)) {
        return 0
    }
    return Math.max(0, Math.min(1, remainingMs / totalMs))
}

export class EmberInvitePrompt extends Container {
    private plate = new Graphics()
    private meter = new Graphics()
    private title: Text
    private body: Text
    private accept = new Graphics()
    private decline = new Graphics()
    private acceptLabel: Text
    private declineLabel: Text
    private onAnswer: (accepted: boolean) => void

    private invite: EmberInvite | null = null
    private remainingMs = 0
    private totalMs = 0

    constructor({ onAnswer }: { onAnswer: (accepted: boolean) => void }) {
        super()
        this.onAnswer = onAnswer

        this.title = new TextRenderer().size("tiny").render("") as Text
        this.title.tint = TITLE_COLOR
        this.title.x = PAD
        this.title.y = PAD - 2

        this.body = new TextRenderer().size("tiny").render("wants you at their camp") as Text
        this.body.tint = BODY_COLOR
        this.body.x = PAD
        this.body.y = this.title.y + LINE_H + LINE_GAP

        const buttonW = Math.round((WIDTH - PAD * 2 - BUTTON_GAP) / 2)
        const buttonY = this.body.y + LINE_H + LINE_GAP + 2
        this.acceptLabel = this.buildButton(this.accept, "Join", PAD, buttonY, buttonW, ACCEPT_TEXT)
        this.declineLabel = this.buildButton(
            this.decline,
            "Decline",
            PAD + buttonW + BUTTON_GAP,
            buttonY,
            buttonW,
            DECLINE_TEXT,
        )

        this.bindButton(this.accept, ACCEPT_RIM, buttonW, true)
        this.bindButton(this.decline, DECLINE_RIM, buttonW, false)

        this.addChild(this.plate, this.title, this.body, this.accept, this.decline, this.meter)
        this.draw()
        this.visible = false
    }

    private buildButton = (button: Graphics, text: string, x: number, y: number, w: number, tint: number): Text => {
        const label = new TextRenderer().size("tiny").render(text) as Text
        label.tint = tint
        label.x = Math.round((w - label.width) / 2)
        label.y = Math.round((BUTTON_H - (label.height || LINE_H)) / 2)
        button.x = x
        button.y = y
        button.addChild(label)
        return label
    }

    private bindButton = (button: Graphics, rim: number, w: number, accepted: boolean) => {
        button.interactive = true
        button.cursor = "pointer"
        // Proud of the fill on every side: these are 16px tall, and one of them moves the player to
        // another room.
        button.hitArea = new Rectangle(-3, -3, w + 6, BUTTON_H + 6)
        const answer = () => this.answer(accepted)
        button.on("click", answer)
        button.on("tap", answer)
        button.on("mouseover", () => this.drawButton(button, rim, w, true))
        button.on("mouseout", () => this.drawButton(button, rim, w, false))
    }

    // Put the prompt up, or take it down with null. A fresh invitation always restarts the clock --
    // a second host asking replaces the first, exactly as the server's own one-per-guest rule does.
    update = (invite: EmberInvite | null) => {
        this.invite = invite || null
        if (!this.invite) {
            this.visible = false
            this.remainingMs = 0
            this.totalMs = 0
            return
        }
        this.totalMs = this.invite.expiresInMs > 0 ? this.invite.expiresInMs : 0
        this.remainingMs = this.totalMs
        this.title.text = `${truncateName(this.invite.fromName)}`
        this.visible = true
        this.draw()
    }

    // Answer it. Public because the buttons are buried in the display tree, and because a caller with
    // a keyboard shortcut or a test has as much right to answer as the pointer does.
    answer = (accepted: boolean) => {
        if (!this.invite) {
            return
        }
        this.invite = null
        this.visible = false
        this.onAnswer(accepted)
    }

    // Drain the clock. Doing nothing is an answer -- at zero the prompt takes itself down, and tells
    // the server nothing: the server is expiring the same invitation on the same clock.
    animate = (dtMs: number) => {
        if (!this.invite || this.totalMs <= 0) {
            return
        }
        this.remainingMs = Math.max(0, this.remainingMs - dtMs)
        if (this.remainingMs <= 0) {
            this.invite = null
            this.visible = false
            return
        }
        this.drawMeter()
    }

    isOpen = (): boolean => !!this.invite
    remaining = (): number => this.remainingMs
    // The name the prompt is showing, for a caller outside the display tree.
    inviterLabel = (): string => this.title.text

    getWidth = (): number => WIDTH
    getHeight = (): number => this.accept.y + BUTTON_H + METER_GAP + METER_H + PAD - 2

    private draw = () => {
        const buttonW = Math.round((WIDTH - PAD * 2 - BUTTON_GAP) / 2)
        this.plate.clear()
        this.plate.beginFill(PANEL_COLOR, PANEL_ALPHA)
        this.plate.lineStyle(1, RIM, RIM_ALPHA)
        this.plate.drawRoundedRect(0, 0, WIDTH, this.getHeight(), CORNER)
        this.plate.endFill()
        this.drawButton(this.accept, ACCEPT_RIM, buttonW, false)
        this.drawButton(this.decline, DECLINE_RIM, buttonW, false)
        this.acceptLabel.x = Math.round((buttonW - this.acceptLabel.width) / 2)
        this.declineLabel.x = Math.round((buttonW - this.declineLabel.width) / 2)
        this.drawMeter()
    }

    private drawButton = (button: Graphics, rim: number, w: number, hover: boolean) => {
        button.clear()
        button.lineStyle(1, rim, hover ? 1 : 0.75)
        button.beginFill(hover ? BUTTON_FILL_HOVER : BUTTON_FILL, 0.95)
        button.drawRoundedRect(0, 0, w, BUTTON_H, 4)
        button.endFill()
    }

    private drawMeter = () => {
        const w = WIDTH - PAD * 2
        const y = this.accept.y + BUTTON_H + METER_GAP
        this.meter.clear()
        this.meter.beginFill(METER_TRACK, 0.9)
        this.meter.drawRoundedRect(PAD, y, w, METER_H, 1.5)
        this.meter.endFill()
        const left = inviteProgress(this.remainingMs, this.totalMs)
        if (left > 0) {
            this.meter.beginFill(RIM, 0.95)
            this.meter.drawRoundedRect(PAD, y, Math.max(1, w * left), METER_H, 1.5)
            this.meter.endFill()
        }
    }
}
